import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { BACKEND_URL } from '../config';
import {
  X,
  Lightning,
  Robot,
  TreeStructure,
  ChatCircleText,
  Gear,
  Phone,
  ArrowUp,
  ArrowDown,
  CircleNotch,
} from '@phosphor-icons/react';

const REASON_CONFIG = {
  agent_execution: { label: 'Agent Run', icon: Robot, color: '#00E5C0' },
  process_analysis: { label: 'Process Analysis', icon: TreeStructure, color: '#00C9A7' },
  org_chart_analysis: { label: 'Org Chart Analysis', icon: TreeStructure, color: '#00C9A7' },
  llm_evaluation: { label: 'LLM Evaluation', icon: ChatCircleText, color: '#00E5C0' },
  automation_execution: { label: 'Automation', icon: Gear, color: '#14F195' },
  voice_call: { label: 'Voice Minutes', icon: Phone, color: '#3B82F6' },
  topup: { label: 'Credit Top-Up', icon: ArrowUp, color: '#14F195' },
  monthly_reset: { label: 'Monthly Reset', icon: Lightning, color: '#14F195' },
  process_extraction: { label: 'Process Extraction', icon: TreeStructure, color: '#00C9A7' },
  PROCESS_DIAGRAM_ANALYSIS: { label: 'Diagram Analysis', icon: TreeStructure, color: '#00C9A7' },
  ORG_CHART_SCAN: { label: 'Org Chart Scan', icon: TreeStructure, color: '#00C9A7' },
};

const getReasonConfig = (reason) => {
  if (REASON_CONFIG[reason]) return REASON_CONFIG[reason];
  // Fallback for unknown reasons
  const label = reason.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());
  return { label, icon: Lightning, color: '#718096' };
};

const formatDate = (dateStr) => {
  const date = new Date(dateStr);
  const now = new Date();
  const diff = now - date;
  const mins = Math.floor(diff / 60000);
  const hours = Math.floor(diff / 3600000);
  const days = Math.floor(diff / 86400000);

  if (mins < 1) return 'Just now';
  if (mins < 60) return `${mins}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const CreditUsagePanel = ({ isOpen, onClose, credits: initialCredits, onBalanceUpdate }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [freshCredits, setFreshCredits] = useState(null);

  const credits = freshCredits || initialCredits;

  useEffect(() => {
    if (!isOpen) return;
    setLoading(true);
    
    // Fetch both balance and history in parallel
    Promise.all([
      fetch(`${BACKEND_URL}/api/credits/balance`, { credentials: 'include' }).then(r => r.json()),
      fetch(`${BACKEND_URL}/api/credits/history?limit=50`, { credentials: 'include' }).then(r => r.json()),
    ])
      .then(([balanceData, historyData]) => {
        if (balanceData.credit_balance !== undefined) {
          setFreshCredits(balanceData);
          if (onBalanceUpdate) onBalanceUpdate(balanceData);
        }
        setTransactions(historyData.transactions || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [isOpen]);

  // Group transactions by date
  const grouped = transactions.reduce((acc, t) => {
    const date = new Date(t.created_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
    if (!acc[date]) acc[date] = [];
    acc[date].push(t);
    return acc;
  }, {});

  // Calculate totals by type for summary
  const summary = transactions.reduce((acc, t) => {
    if (t.amount < 0) {
      const cfg = getReasonConfig(t.reason);
      acc[cfg.label] = (acc[cfg.label] || 0) + Math.abs(t.amount);
    }
    return acc;
  }, {});

  const totalUsed = transactions.reduce((sum, t) => t.amount < 0 ? sum + Math.abs(t.amount) : sum, 0);

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[9999]"
            onClick={onClose}
          />
          <motion.div
            initial={{ x: '-100%', opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            exit={{ x: '-100%', opacity: 0 }}
            transition={{ type: 'spring', damping: 25, stiffness: 300 }}
            className="fixed left-0 top-0 bottom-0 w-[400px] bg-[#0D0E12] border-r border-[var(--border-default)] z-[10000] flex flex-col"
            data-testid="credit-usage-panel"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-5 border-b border-[var(--border-default)]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg bg-[#00C9A7] flex items-center justify-center">
                  <Lightning size={18} weight="duotone" className="text-white" />
                </div>
                <div>
                  <h2 className="text-sm font-semibold text-white">Credit Usage</h2>
                  <p className="text-[11px] text-[#718096]">Last 30 days</p>
                </div>
              </div>
              <button onClick={onClose} className="text-[#718096] hover:text-white transition-colors" data-testid="close-credit-panel">
                <X size={18} />
              </button>
            </div>

            {/* Balance Summary */}
            {credits && (
              <div className="px-5 py-4 border-b border-[var(--border-default)]">
                <div className="flex items-baseline justify-between mb-2">
                  <span className="text-2xl font-bold text-white">{credits.credit_balance?.toLocaleString()}</span>
                  <span className="text-xs text-[#718096]">of {credits.monthly_credit_limit?.toLocaleString()} monthly</span>
                </div>
                <div className="w-full h-2 rounded-full bg-[rgba(255,255,255,0.06)] overflow-hidden">
                  <div
                    className="h-full rounded-full bg-[#00E5C0] transition-all"
                    style={{ width: `${Math.min(100, credits.monthly_credit_limit > 0 ? (credits.credit_balance / credits.monthly_credit_limit) * 100 : 0)}%` }}
                  />
                </div>
                {totalUsed > 0 && (
                  <p className="text-[11px] text-[#718096] mt-2">{totalUsed.toLocaleString()} credits used in this period</p>
                )}
              </div>
            )}

            {/* Usage Breakdown */}
            {Object.keys(summary).length > 0 && (
              <div className="px-5 py-3 border-b border-[var(--border-default)]">
                <p className="text-[10px] font-semibold text-[#718096] uppercase tracking-wider mb-2">Breakdown</p>
                <div className="space-y-1.5">
                  {Object.entries(summary).sort((a, b) => b[1] - a[1]).slice(0, 5).map(([label, amount]) => (
                    <div key={label} className="flex items-center justify-between">
                      <span className="text-xs text-[#A0AEC0]">{label}</span>
                      <span className="text-xs font-medium text-white">{amount.toLocaleString()} credits</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Transaction History */}
            <div className="flex-1 overflow-y-auto">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <CircleNotch size={24} className="animate-spin text-[#00E5C0]" />
                </div>
              ) : transactions.length === 0 ? (
                <div className="flex flex-col items-center justify-center py-12 px-5 text-center">
                  <Lightning size={32} className="text-[#718096] mb-3" />
                  <p className="text-sm text-[#A0AEC0]">No usage yet</p>
                  <p className="text-xs text-[#718096] mt-1">Credit usage from AI actions will appear here</p>
                </div>
              ) : (
                <div className="px-5 py-3">
                  <p className="text-[10px] font-semibold text-[#718096] uppercase tracking-wider mb-3">History</p>
                  {Object.entries(grouped).map(([date, txns]) => (
                    <div key={date} className="mb-4">
                      <p className="text-[10px] font-medium text-[#555] mb-1.5">{date}</p>
                      <div className="space-y-1">
                        {txns.map((t, i) => {
                          const cfg = getReasonConfig(t.reason);
                          const Icon = cfg.icon;
                          const isDeduction = t.amount < 0;
                          return (
                            <div key={i} className="flex items-center gap-3 py-1.5 px-2 rounded-lg hover:bg-[rgba(255,255,255,0.03)] transition-colors">
                              <div className="w-7 h-7 rounded-md flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${cfg.color}15` }}>
                                <Icon size={14} style={{ color: cfg.color }} />
                              </div>
                              <div className="flex-1 min-w-0">
                                <p className="text-xs text-white truncate">{cfg.label}</p>
                                <p className="text-[10px] text-[#555]">{formatDate(t.created_at)}</p>
                              </div>
                              <div className="text-right flex-shrink-0">
                                <span className={`text-xs font-semibold ${isDeduction ? 'text-[#EF4444]' : 'text-[#14F195]'}`}>
                                  {isDeduction ? '' : '+'}{t.amount}
                                </span>
                                <p className="text-[9px] text-[#555]">bal: {t.balance_after?.toLocaleString()}</p>
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default CreditUsagePanel;
